import * as ModelEvent from "@node-elion/syncron";

import SubscriptionPool from "../../redux/services/SubscriptionPool";
import createLogger from "../../utils/logger.util";
import { NonEditableProperties } from "../../types/NonEditableProperties";
import OptionsBase from "../../types/ServiceSubscribeOptionsBase";
import Subscription from "../../types/Subscription";
import { TaxiServiceOld as TaxiService, Base, Language, Company } from "..";

import {
	createObjectLanguageNamesFromObject,
} from "../../assets/languages/langs";

import {
	ReceiptBindingTarget,
	ReceiptCashRegister,
	ReceiptOrderPaymentType,
	ReceiptOrderSource,
	ReceiptSystemProviderEnum,
	normalizeReceiptOrderPaymentTypes,
} from "./types";

const logger = createLogger({ name: "ReceiptProvider" });

/**
 * Сервис-обертка над PRPC-эндпоинтами `receipt.provider.*`.
 * По духу копирует `SMSProvider` (subscribe + CRUD) и добавляет два метода:
 *   - `checkConnection` (как у `PaymentSystem.connectCheck`)
 *   - `getHistory` (как у `SMSProvider.getHistory` — версии provider snapshots)
 *
 * Используется на странице настроек `Settings/ReceiptProvider`
 * и в карточках водителя/машины (через redux-обертку).
 */
class ReceiptProvider extends Base {
	public static fromResponse(data: any): ReceiptProvider.Model {
		const wireTaxiServiceIds = Array.isArray(data.taxiServiceIds)
			? data.taxiServiceIds.filter(
					(id: unknown): id is number => typeof id === "number",
			  )
			: [];

		const links = Array.isArray(data.receiptProviderToTaxiServices)
			? data.receiptProviderToTaxiServices
			: [];

		const taxiServiceIds =
			wireTaxiServiceIds.length > 0
				? wireTaxiServiceIds
				: links
						.map(
							(item: any) =>
								item?.taxiService?.id ??
								item?.taxiServiceId ??
								null,
						)
						.filter((id): id is number => typeof id === "number");

		const taxiServices =
			wireTaxiServiceIds.length > 0 || links.length === 0
				? []
				: links
						.map((item: any) => item?.taxiService)
						.filter(Boolean)
						.map(TaxiService.fromResponse);

		const wireCompanyIds = Array.isArray(data.companyIds)
			? data.companyIds.filter(
					(id: unknown): id is number => typeof id === "number",
			  )
			: [];

		const companiesById = new Map<number, Company.Model>();
		taxiServices.forEach((ts: any) => {
			const company = ts?.company;
			if (company?.id) companiesById.set(company.id, company);
		});
		links.forEach((link: any) => {
			const rawCompany = link?.taxiService?.company;
			if (rawCompany?.id) {
				companiesById.set(rawCompany.id, Company.fromResponse(rawCompany));
			}
		});

		const companies = Array.from(companiesById.values());
		const companyIds =
			wireCompanyIds.length > 0
				? wireCompanyIds
				: companies.map((c) => c.id);

		return {
			id: data.id,
			name: data.name,
			provider:
				data.provider || ReceiptSystemProviderEnum.FISCAL_PARTNER,
			active: !!data.active,
			host: data.host || "",
			apiKey: data.apiKey || "",
			configuration: data.configuration ?? null,
			orderSources: data.orderSources ?? [],
			paymentTypes: normalizeReceiptOrderPaymentTypes(data.paymentTypes),
			bindingTarget:
				data.bindingTarget || ReceiptBindingTarget.EXECUTOR,
			enableByDefault: !!data.enableByDefault,
			description: data.description ?? "",
			sendSmsEnabled: !!data.sendSmsEnabled,
			smsOrderSources: data.smsOrderSources ?? [],
			smsProviderId: data.smsProviderId ?? null,
			smsMessageTexts: data.smsMessageTexts ?? {},
			taxiServices,
			taxiServiceIds,
			companyIds,
			companies,
			cashRegisters: Array.isArray(data.cashRegisters)
				? data.cashRegisters.map(
						(item: any): ReceiptCashRegister => ({
							id: item.id,
							name: item.name ?? "",
							licenseKey: item.licenseKey ?? "",
							pinCode: item.pinCode ?? "",
							active: item.active !== false,
							executorIds: Array.isArray(item.executorIds)
								? item.executorIds
								: [],
							carIds: Array.isArray(item.carIds)
								? item.carIds
								: [],
						}),
				  )
				: [],

			createdAt: data.createdAt,
			updatedAt: data.updatedAt,
			deletedAt: data.deletedAt,
		};
	}

	private static toRequest(model: ReceiptProvider.Modified) {
		return {
			name: model.name,
			provider:
				model.provider ?? ReceiptSystemProviderEnum.FISCAL_PARTNER,
			active: model.active,
			host: (model.host ?? "").trim(),
			apiKey: (model.apiKey ?? "").trim(),
			configuration: model.configuration ?? null,
			orderSources: model.orderSources ?? [],
			paymentTypes: normalizeReceiptOrderPaymentTypes(model.paymentTypes),
			bindingTarget: model.bindingTarget,
			enableByDefault: model.enableByDefault ?? false,
			description: model.description ?? null,
			sendSmsEnabled: model.sendSmsEnabled ?? false,
			smsOrderSources: model.smsOrderSources ?? [],
			smsProviderId: model.smsProviderId ?? null,
			smsMessageTexts: model.smsMessageTexts ?? null,
			taxiServiceIds:
				model.taxiServiceIds ??
				model.taxiServices?.map((ts) => ts.id) ??
				[],
			...(model.cashRegisters
				? { cashRegisters: model.cashRegisters }
				: {}),
		};
	}

	public static async getAll(): Promise<ReceiptProvider.Model[] | Error> {
		try {
			const res = await this.request((prpc) =>
				prpc.theirsModel.receipt.provider.getAll(),
			);
			return res?.items?.map(this.fromResponse) ?? [];
		} catch (error) {
			return error as Error;
		}
	}

	public static async getById(
		id: number,
	): Promise<ReceiptProvider.Model | null> {
		try {
			const res = await this.request((prpc) =>
				prpc.theirsModel.receipt.provider.getById(id),
			);
			return res ? this.fromResponse(res) : null;
		} catch (error) {
			logger.error("[ReceiptProvider] getById error", error);
			return null;
		}
	}

	public static async create(model: ReceiptProvider.Modified) {
		try {
			const res = await this.request((prpc) =>
				prpc.theirsModel.receipt.provider.create(this.toRequest(model)),
			);
			return res ? this.fromResponse(res) : null;
		} catch (error) {
			logger.error("[ReceiptProvider] create error", error);
			return null;
		}
	}

	public static async update(model: ReceiptProvider.Modified) {
		try {
			if (!model.id) return null;
			const res = await this.request((prpc) =>
				prpc.theirsModel.receipt.provider.update(
					model.id,
					this.toRequest(model),
				),
			);
			return res ? this.fromResponse(res) : null;
		} catch (error) {
			logger.error("[ReceiptProvider] update error", error);
			return null;
		}
	}

	public static async remove(id: number) {
		try {
			await this.request((prpc) =>
				prpc.theirsModel.receipt.provider.delete(id),
			);
			return true;
		} catch (error) {
			logger.error("[ReceiptProvider] remove error", error);
			return false;
		}
	}

	/**
	 * Проверка подключения к ECC по введенным host/apiKey.
	 * Возвращает `connected` boolean + детали ошибки.
	 */
	public static async checkConnection(params: {
		host: string;
		apiKey: string;
		provider?: ReceiptSystemProviderEnum;
		/** Checkbox: `login`/`password` кассира. */
		configuration?: Record<string, any> | null;
	}): Promise<{
		connected: boolean;
		statusCode: number | null;
		message: string;
	} | null> {
		try {
			const res = await this.request((prpc) =>
				prpc.theirsModel.receipt.provider.checkConnection({
					host: params.host.trim(),
					apiKey: params.apiKey.trim(),
					...(params.provider ? { provider: params.provider } : {}),
					...(params.configuration
						? { configuration: params.configuration }
						: {}),
				}),
			);
			return res;
		} catch (error) {
			logger.error("[ReceiptProvider] checkConnection error", error);
			return null;
		}
	}

	public static async getHistory(id: number) {
		try {
			const res = await this.request((prpc) =>
				prpc.theirsModel.receipt.provider.getHistory(id),
			);
			return res ?? [];
		} catch (error) {
			logger.error("[ReceiptProvider] getHistory error", error);
			return [];
		}
	}

	public static async subscribe(
		options: ReceiptProvider.SubscribeOptions,
		onUpdate: Subscription.OnUpdate<ReceiptProvider.Model>,
	): Promise<Subscription<ReceiptProvider.SubscribeOptions> | null> {
		const modelEventConstructor = new ModelEvent.ModelEventConstructor({
			onUpdate: (state) => {
				logger.info("[ReceiptProvider] subscribe", { options, state });
				onUpdate({
					...state,
					models: state.models.map(this.fromResponse),
				});
			},
		});

		const subscription = await SubscriptionPool.add(
			(prpc) =>
				prpc.theirsModel.receipt.provider.subscribe({
					params: options,
					ping: () => true,
					onEvent: (events) => {
						modelEventConstructor.onEvent(events);
					},
					onError: (error) => {
						logger.error(error);
					},
				}),
			{ name: "ReceiptProvider.subscribe" },
		);

		return {
			unsubscribe: () => subscription.unsubscribe(),
			update: (next: ReceiptProvider.SubscribeOptions) =>
				subscription.update(next),
		};
	}
}

declare namespace ReceiptProvider {
	interface Model extends NonEditableProperties {
		name: string;
		provider: ReceiptSystemProviderEnum;
		active: boolean;
		host: string;
		apiKey: string;
		configuration: Record<string, any> | null;
		orderSources: ReceiptOrderSource[];
		paymentTypes: ReceiptOrderPaymentType[];
		bindingTarget: ReceiptBindingTarget;
		enableByDefault: boolean;
		description: string;
		sendSmsEnabled: boolean;
		smsOrderSources: ReceiptOrderSource[];
		smsProviderId: number | null;
		smsMessageTexts: Record<Language, string>;
		taxiServices: TaxiService.Model[];
		taxiServiceIds: number[];
		companyIds: number[];
		companies: Company.Model[];
		cashRegisters: ReceiptCashRegister[];
	}

	interface Modified extends Partial<Model> {
		id?: number;
		taxiServiceIds?: number[];
		companyIds?: number[];
	}

	interface SubscribeOptions extends OptionsBase<ReceiptProvider.Model> {
		active?: boolean;
		lang?: Language;
		bindingTarget?: ReceiptBindingTarget;
		taxiServiceIds?: number[];
		companyIds?: number[];
	}
}

export default ReceiptProvider;
